// BenchSections — Unassigned, Home and Traveling, organised the way the board
// organises them.
//
// ⚠️ WHY THESE ARE SECTIONS AND NOT COUNTS.
//
// The first version of this panel reduced the bench to a number in a status
// rail ("1 free"). The board does not do that: it renders Unassigned, Home and
// Traveling as their own tinted sections with the people named in them, and it
// keeps doing so in TV mode.
//
// The reason matters. A provider who is off a room, not on the bench, not
// marked departed and not travelling is INVISIBLE — nobody calls them and they
// go home on their own. That is the single failure the placement flow's
// required-decision guardrail exists to prevent, and a panel that shows only a
// count cannot show you it has happened. Naming people is the check.

import React from "react";
import styles, { C } from "./panelCard.styles";
import { onCallChipLabel, onCallChipTitle } from "../../domain/onCallChip";

const TINT = {
  unassigned: "#ede9fe",
  home: "#eef2f7",
  traveling: "#e0f2fe",
};

// OB CALL — a QUIET chip, deliberately. The person is on this list because they
// hold the standing on-call watch and are free between epidurals and sections;
// that is why they are free, not an alarm about them. Teal outline, matching
// `SHIFT_COLOR_ON_CALL` (domain/timeline.js) so "on call" reads the same here as
// it does wherever a shift is labelled. Red and any fill are reserved for
// emergencies.
function OnCallChip({ room }) {
  const label = onCallChipLabel(room);
  // Only the OB watch is a duty worth badging; anything else renders nothing.
  if (!label) return null;
  return (
    <span
      title={onCallChipTitle(room)}
      style={{
        fontSize: 9.5,
        fontWeight: 800,
        letterSpacing: ".5px",
        color: "#0f766e",
        border: "1px solid #0f766e",
        borderRadius: 999,
        padding: "1px 7px",
        whiteSpace: "nowrap",
        flexShrink: 0,
      }}
    >
      {label}
    </span>
  );
}

// GIVING BREAKS — the other reason a free person is not spare. Same chip shape
// as OB CALL because it is the same kind of fact; amber because that is the
// board's break colour everywhere else. Outline only: a duty, not an alarm.
function GivingBreaksChip() {
  return (
    <span
      title="Giving breaks — free, but covering the break rounds"
      style={{
        fontSize: 9.5,
        fontWeight: 800,
        letterSpacing: ".5px",
        color: "#7a4f00",
        border: "1px solid #f59e0b",
        borderRadius: 999,
        padding: "1px 7px",
        whiteSpace: "nowrap",
        flexShrink: 0,
      }}
    >
      GIVING BREAKS
    </span>
  );
}

function PersonRow({ name, detail, badge, action }) {
  return (
    <li
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        background: C.card,
        border: `1px solid ${C.line}`,
        borderRadius: 8,
        padding: "7px 10px",
        minHeight: 40,
      }}
    >
      <span style={{ fontWeight: 700, fontSize: 13 }}>{name}</span>
      {detail ? <span style={{ fontSize: 11.5, color: C.muted }}>{detail}</span> : null}
      {badge || null}
      {action ? <span style={{ marginLeft: "auto" }}>{action}</span> : null}
    </li>
  );
}

function Bucket({ title, tint, people, empty }) {
  if (!people.length) return null;
  return (
    <section style={{ ...styles.section, background: tint }}>
      <h2 style={styles.sectionHead}>
        {title}
        <span style={styles.countChip}>{people.length}</span>
      </h2>
      <ul
        style={{
          listStyle: "none",
          margin: 0,
          padding: "0 0 12px",
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
          gap: 8,
        }}
      >
        {people.length ? people : <li style={{ color: C.muted, fontSize: 12.5 }}>{empty}</li>}
      </ul>
    </section>
  );
}

export default function BenchSections({
  bench = [],
  doctors = [],
  departed = [],
  traveling = [],
  canAct,
  onAssign,
}) {
  const unassigned = [
    ...bench.map((p) => (
      <PersonRow
        key={`p-${p.id}`}
        name={p.name}
        detail={String(p.subRole || "CRNA").toUpperCase()}
        // `onCall` rides in on the bench entry (`benchProviders`), which sets it
        // from the SAME predicate the chart uses to decide this person draws no
        // bar. Nothing here re-asks "are they on call".
        // Both can be true at once — the OB watch can be the one walking the
        // rounds — so this renders BOTH chips rather than picking a winner.
        badge={
          p.onCall || p.givingBreaks ? (
            <>
              {p.onCall ? <OnCallChip room={p.onCallRoom} /> : null}
              {p.givingBreaks ? <GivingBreaksChip /> : null}
            </>
          ) : null
        }
        action={
          canAct ? (
            <button type="button" style={styles.act} onClick={() => onAssign(p)}>
              Assign
            </button>
          ) : null
        }
      />
    )),
    ...doctors.map((d) => (
      <PersonRow key={`d-${d.id}`} name={d.name} detail="MD" />
    )),
  ];

  return (
    <>
      <Bucket title="Unassigned" tint={TINT.unassigned} people={unassigned} empty="Nobody free." />
      <Bucket
        title="Home"
        tint={TINT.home}
        people={departed.map((m) => (
          <PersonRow key={`h-${m.id}`} name={m.name} detail="departed" />
        ))}
        empty="Nobody sent home."
      />
      <Bucket
        title="Traveling"
        tint={TINT.traveling}
        people={traveling.map((t) => (
          <PersonRow
            key={`t-${t.id}`}
            name={t.providerName || "Provider"}
            // Naming the destination matters: the trip stays open until someone
            // at the far end confirms the arrival, and this is the only place
            // this board can show that it is still open.
            detail={`→ ${t.toFacilityName || t.toFacilityId || "another facility"}`}
          />
        ))}
        empty="Nobody traveling."
      />
    </>
  );
}
